import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getToken, getRefreshToken, storeTokens, clearTokens } from '../../utils/auth.utils';
import { logout, updateTokens } from '../slices/user.slice';
import { addApiCall } from '../slices/apiHistory.slice';
import { encryptPayload, decryptPayload } from '../../utils/encryption.utils';
import { normalizeApiErrorResult } from '../../utils/api-error.utils';
import { AnalyticsService, classifyApiError, endpointToFeature } from 'services/analytics';

/**
 * Base API configuration for RTK Query
 * All API slices should extend this base configuration
 * 
 * Centralized encryption/decryption:
 * - Automatically encrypts request bodies when VITE_ENABLE_ENCRYPTION=true
 * - Automatically decrypts response data
 * - Handles both success and error response decryption
 */

// Check if encryption should be enabled
const shouldEncrypt = import.meta.env.VITE_ENABLE_ENCRYPTION === 'true';

// Create a custom base query with error handling and encryption
const baseQueryWithAuth = async (args, api, extraOptions) => {
  // Create the base query
  const baseQuery = fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_BASE_URL,
    prepareHeaders: (headers) => {
      // Get token from localStorage
      const token = getToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  });

  // Encrypt request body if encryption is enabled
  if (shouldEncrypt && args.body && typeof args.body === 'object') {
    args = {
      ...args,
      body: { encrypted: encryptPayload(args.body) },
    };
  }

  // Execute the query
  let result = await baseQuery(args, api, extraOptions);

  // Replace raw browser errors (Failed to fetch, TypeError, etc.) with a friendly network message
  result = normalizeApiErrorResult(result);

  // Track API failures in GA4
  if (result.error) {
    const endpoint = typeof args === 'string' ? args : args?.url ?? '';
    const status = result.error.status;
    AnalyticsService.trackApiFailure(
      endpoint,
      status,
      classifyApiError(status),
      endpointToFeature(endpoint)
    );
  }

  // Decrypt response if encryption is enabled (before recording so history shows decrypted response)
  if (shouldEncrypt && result.data?.encrypted) {
    result.data = decryptPayload(result.data.encrypted);
  }
  if (shouldEncrypt && result.error?.data?.encrypted) {
    result.error.data = decryptPayload(result.error.data.encrypted);
  }

  // Handle auto-refreshing notifications after action completion (any successful non-GET request)
  {
    const method = (typeof args === 'object' && args?.method) ? args.method.toUpperCase() : 'GET';
    const originalUrl = typeof args === 'string' ? args : args?.url ?? '';
    const isNotificationEndpoint = originalUrl.includes('/notification/');

    // If it's a successful mutation, and not a notification endpoint itself
    if (method !== 'GET' && !result.error && !isNotificationEndpoint) {
      // Invalidate the Notifications tag so the notification list/counts refreshes
      //api.dispatch({ type: 'api/invalidateTags', payload: ['Notifications'] 
      // NOTE: Using the utility function is the correct way to trigger refetches
      api.dispatch(baseApi.util.invalidateTags(['Notifications']));
    }
  }

  // Record API call for history (mobile debug / Show API History) - skip refresh-token
  const url = typeof args === 'string' ? args : args?.url ?? '';
  if (!url.includes('refresh-token')) {
    const method = (typeof args === 'object' && args?.method) ? args.method.toUpperCase() : 'GET';
    const status = result.error ? result.error.status : (result.meta?.response?.status ?? 200);
    let responseSummary = result.error
      ? (result.error.data?.message ?? result.error.data?.error ?? result.error.status ?? 'Error')
      : (result.data ? 'OK' : '—');
    if (typeof responseSummary !== 'string') {
      responseSummary = typeof responseSummary === 'object' ? JSON.stringify(responseSummary).slice(0, 80) : String(responseSummary);
    }
    const rawBody = result.error ? result.error.data : result.data;
    let responseBody = null;
    if (rawBody != null) {
      try {
        responseBody = typeof rawBody === 'string' ? rawBody : JSON.stringify(rawBody, null, 2);
        if (responseBody.length > 5000) responseBody = responseBody.slice(0, 5000) + '\n... (truncated)';
      } catch (_) {
        responseBody = String(rawBody);
      }
    }
    api.dispatch(addApiCall({ method, url, status, response: responseSummary, responseBody }));
  }

  // Handle 401 Unauthorized errors - attempt token refresh
  if (result.error && result.error.status === 401) {
    const originalUrl = args.url || '';

    // Skip token refresh for auth endpoints (signup, login, refresh-token, etc.)
    const isAuthEndpoint = originalUrl.includes('/auth/signup') ||
      originalUrl.includes('/auth/login') ||
      originalUrl.includes('/auth/refresh-token');
    // kept as per narins discussion 
    // ||
    // originalUrl.includes('/auth/send-otp') ||
    // originalUrl.includes('/auth/verify-otp');

    if (isAuthEndpoint) {
      // For auth endpoints, just logout and redirect

      clearTokens();
      api.dispatch(logout());
      window.location.href = '/login';
      return result;
    }

    // Get refreshToken from localStorage or Redux state
    let refreshToken = getRefreshToken();
    const currentToken = getToken();

    // Fallback: Get refreshToken from Redux state if not in localStorage
    if (!refreshToken) {
      const state = api.getState();
      refreshToken = state?.user?.tokens?.refreshToken || state?.user?.profile?.tokens?.refreshToken;
    }

    // Check if we have a refresh token
    if (!refreshToken) {
      clearTokens();
      api.dispatch(logout());
      window.location.href = '/login';
      return result;
    }


    try {
      // Call refresh token API directly using fetch
      const refreshResult = await fetch(`${import.meta.env.VITE_API_BASE_URL}api/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: refreshToken
        }),
      });

      const refreshData = await refreshResult.json();

      // Check if refresh was successful (code: CAUS0005)
      if (refreshData?.code === 'CAUS0005' && refreshData?.result?.accessToken) {
        // Store the new access token
        const newAccessToken = refreshData.result.accessToken;
        const expiresIn = refreshData.result.expiresIn || 3600;

        // Update tokens in localStorage
        storeTokens({
          token: newAccessToken,
          refreshToken: refreshToken // Keep the same refresh token
        });

        // Update tokens in Redux state
        api.dispatch(updateTokens({
          accessToken: newAccessToken,
          refreshToken: refreshToken,
          expiresIn: expiresIn
        }));



        // Prepare retry args (handle encryption if enabled)
        let retryArgs = { ...args };
        if (shouldEncrypt && retryArgs.body && typeof retryArgs.body === 'object') {
          retryArgs = {
            ...retryArgs,
            body: { encrypted: encryptPayload(retryArgs.body) },
          };
        }

        // Create a new base query instance with updated token for retry
        const retryBaseQuery = fetchBaseQuery({
          baseUrl: import.meta.env.VITE_API_BASE_URL,
          prepareHeaders: (headers) => {
            headers.set('Authorization', `Bearer ${newAccessToken}`);
            return headers;
          },
        });

        // Retry the original request with new token
        const retryResult = await retryBaseQuery(retryArgs, api, extraOptions);

        // Decrypt retry response if needed
        if (shouldEncrypt && retryResult.data?.encrypted) {
          retryResult.data = decryptPayload(retryResult.data.encrypted);
        }

        if (shouldEncrypt && retryResult.error?.data?.encrypted) {
          retryResult.error.data = decryptPayload(retryResult.error.data.encrypted);
        }

        return normalizeApiErrorResult(retryResult);
      } else {
        // Refresh failed
        throw new Error('Token refresh failed');
      }
    } catch (refreshError) {
      console.error('❌ Token refresh error:', refreshError);
      // If refresh fails, logout user
      clearTokens();
      api.dispatch(logout());
      window.location.href = '/login';
      return result;
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'User',
    'Customer',
    'OTP',
    'SupportRequest',
    'RiskAssessment',
    'AccountAggregator',
    'MutualFunds',
    'Portfolio',
    'SIPs',
    'Notifications',
    'Nominee',
    'BankLinking',
    'KYC',
    'FATCA',
    'InvestmentLumpsum',
    'InvestmentSip',
    'Redemption',
    'Reports',
    'Transactions',
    'UCCRegistration',
    'Dashboard',
    'Analytics',
    'Settings',
    'Orders',
    'Elog',
    'FundCard',
    'MutualFundsDetails',
    'MutualFundsHoldings',
    'MutualFundsHistory',
    'BankingAccounts',
    'InvestmentPortfolio',
    'Consents',
    'InsurancePolicies',
    'SubscriptionPlans',
    'SubscriptionCurrent',
    'SubscriptionPaymentHistory',
  ],
  endpoints: () => ({}),
});

// No hooks exported yet as we don't have any endpoints defined
